import { shallowRef, type ShallowRef } from 'vue';
import type { UploadedVaultRevision } from '@/composables/useVaultUpload';

export interface VaultUploadDialogOptions {
  /** Files already chosen (a drop, a quick action); the user can still add or remove. */
  files?: File[];
  /** Keep the destination at or below this folder (e.g. a review project's data room). */
  scopeRootUid?: string;
  /** That folder's name, the picker's first crumb. */
  scopeRootLabel?: string;
  accept?: string;
  max?: number;
}

interface VaultUploadDialogRequest extends VaultUploadDialogOptions {
  resolve: (files: UploadedVaultRevision[] | null) => void;
}

// One dialog for the whole app, mounted by VaultUploadDialogHost in App.vue.
const request = shallowRef<VaultUploadDialogRequest | null>(null);

/**
 * Ask the user where files go before uploading them — for uploads started
 * outside the vault, which have no folder of their own. Resolves with what
 * landed, in list order, or null when the dialog is closed without uploading.
 */
export function useVaultUploadDialog(): {
  openUploadDialog: (options?: VaultUploadDialogOptions) => Promise<UploadedVaultRevision[] | null>;
} {
  function openUploadDialog(
    options: VaultUploadDialogOptions = {}
  ): Promise<UploadedVaultRevision[] | null> {
    // A second request supersedes one still open; that caller sees a cancel.
    request.value?.resolve(null);
    return new Promise(resolve => {
      request.value = { ...options, resolve };
    });
  }

  return { openUploadDialog };
}

/** The host's side: the open request, and settling it. */
export function useVaultUploadDialogHost(): {
  request: ShallowRef<VaultUploadDialogRequest | null>;
  settle: (files: UploadedVaultRevision[] | null) => void;
} {
  function settle(files: UploadedVaultRevision[] | null): void {
    const current = request.value;
    if (!current) return;
    request.value = null;
    current.resolve(files);
  }

  return { request, settle };
}
